<template>
    <LayoutBase>
        <form class="c-event-form form" @submit.prevent="validateBeforeSubmit" novalidate>
            <ValidationAlert :error-list="errors.collect('server')" />
            <header id="content__header">
                <h1>{{ event.location }} Conference Registration</h1>
            </header>

            <div class="form__group">
                <legend class="form__label">Attendant</legend>
                <div class="c-event-form__user">
                    <img class="avatar c-event-form__avatar" :src="`/images/avatar/${user.id}`" alt="">
                    <span>{{ user.firstName }} {{ user.lastName }}</span>
                </div>
            </div>

            <div class="form__group">
                <FormControlDatepicker
                    required
                    v-validate="`date_format:yyyy-MM-dd|date_between:${monthStart},${monthEnd}|required`"
                    :error="errors.first('arrivalDate')"
                    name="arrivalDate"
                    :min-date="monthStartDate"
                    :max-date="endDate"
                    v-model="arrivalDate"
                    label="Arrival Date"
                />
            </div>

            <div class="form__group">
                <FormControlDatepicker
                    required
                    v-validate="`date_format:yyyy-MM-dd|date_between:${monthStart},${monthEnd}|required`"
                    :error="errors.first('departureDate')"
                    name="departureDate"
                    :min-date="startDate"
                    :max-date="monthEndDate"
                    v-model="departureDate"
                    label="Departure Date"
                />
            </div>

            <div class="form__group">
                <label for="adults" class="form__label">Guests - Adults</label>
                <div class="form__increment">
                    <button class="btn btn--increment" @click.prevent.self="" @click="decrementAdults" :disabled="adultGuests <= 0">
                        -
                    </button>
                    <input
                        id="adults"
                        type="number"
                        name="adults"
                        min="1"
                        max="9"
                        class="form__control form__guests"
                        v-model="adultGuests"
                        placeholder="Enter company's name here..."
                        v-validate
                        required
                    />
                    <button class="btn btn--increment" @click.prevent.self="" @click="incrementAdults" :disabled="adultGuests >= 9">
                        +
                    </button>
                </div>
                <div class="form__validation">{{ errors.first('adults') }}</div>
            </div>

            <div class="form__group">
                <label for="children" class="form__label">Guests - Children</label>
                <div class="form__increment">
                    <button class="btn btn--increment" @click.prevent.self="" @click="decrementChildren" :disabled="childGuests <= 0">
                        -
                    </button>
                    <input
                        id="children"
                        type="number"
                        name="children"
                        min="0"
                        max="9"
                        class="form__control form__guests"
                        v-model="childGuests"
                        placeholder="Enter company's name here..."
                        v-validate
                        required
                    />
                    <button class="btn btn--increment" @click.prevent.self="" @click="incrementChildren" :disabled="childGuests >= 9">
                        +
                    </button>
                </div>
                <div class="form__validation">{{ errors.first('children') }}</div>
            </div>

            <div class="form__group">
                <textarea
                    id="dietaryRequirements"
                    name="dietaryRequirements"
                    class="form__control"
                    v-model="dietaryRequirements"
                    placeholder="Please specify..."
                    v-validate
                />
                <label for="dietaryRequirements" class="form__label">Dietary Requirements</label>
                <div class="form__validation">{{ errors.first('dietaryRequirements') }}</div>
            </div>

            <div class="form__group">
                <div class="toggle-group">
                    <input
                        id="accommodation"
                        name="accommodation"
                        class="toggle"
                        type="radio"
                        :value="true"
                        v-model="requiresAccommodation"
                        v-validate
                        required
                    />
                    <label for="accommodation" class="toggle">Yes</label>
                    <input
                        id="no-accommodation"
                        name="accommodation"
                        class="toggle"
                        type="radio"
                        :value="false"
                        v-model="requiresAccommodation"
                        v-validate
                        required
                    />
                    <label for="no-accommodation" class="toggle">No</label>
                </div>
                <label class="form__label">Accommodation required?</label>
                <div class="form__validation">{{ errors.first('is opportunity') }}</div>
            </div>

            <div class="form__group">
                <textarea
                    id="roomRequirements"
                    name="roomRequirements"
                    class="form__control"
                    v-model="roomRequirements"
                    placeholder="Please specify..."
                    v-validate
                />
                <label for="roomRequirements" class="form__label">Room requirements</label>
                <div class="form__validation">{{ errors.first('roomRequirements') }}</div>
            </div>

            <div class="form__group form__group--submit">
                <button class="btn btn--primary" v-wait:disabled='"submit"'>Sign up</button>
            </div>
        </form>
    </LayoutBase>
</template>

<script>

//TODO: Do not allow more than 1 registration per user per event

import LayoutBase from './Layout/LayoutBase.vue';
import { mapGetters, mapState } from 'vuex';
import FormControlDatepicker from './Form/Control/FormControlDatepicker.vue';
import API from './../api';
import { format } from 'date-fns';

export default {
    name: 'PageEvent',
    components: {
        FormControlDatepicker,
        LayoutBase
    },
    data() {
        return {
            registration: this.$store.getters.registration,
            startDate: new Date(this.$store.getters.event.start.unixTimestampMilli),
            endDate: new Date(this.$store.getters.event.end.unixTimestampMilli),
        }
    },
    props: {
        event: String
    },
    computed: {
        monthStart() {
            const start = new Date(this.startDate.getFullYear(), this.startDate.getMonth(), 1);
            return format(Date.parse(start), 'yyyy-MM-dd');
        },
        monthEnd() {
            const end = new Date(this.endDate.getFullYear(), this.endDate.getMonth() + 1, 0);
            return format(Date.parse(end), 'yyyy-MM-dd');
        },
        monthStartDate() {
            return new Date(Date.parse(this.monthStart));
        },
        startDate() {
            return new Date(Date.parse(this.endDate), 'yyyy-MM-dd');
        },
        monthEndDate() {
            return new Date(Date.parse(this.monthEnd));
        },
        endDate() {
            return new Date(Date.parse(this.endDate), 'yyyy-MM-dd');
        },
        adultGuests: {
            get() {
                return this.$store.getters.adultGuests;
            },
            set(val) {
                return this.$store.commit('modifyAdultGuests', val)
            }
        },
        childGuests: {
            get() {
                return this.$store.getters.childGuests;
            },
            set(val) {
                return this.$store.commit('modifyChildGuests', val);
            }
        },
        arrivalDate: {
            get() {
                return this.$store.getters.arrivalDate;
            },
            set(val) {
                return this.$store.commit('modifyArrivalDate', val);
            }
        },
        departureDate: {
            get() {
                return this.$store.getters.departureDate;
            },
            set(val) {
                return this.$store.commit('modifyDepartureDate', val);
            }
        },
        requiresAccommodation: {
            get() {
                return this.$store.getters.requiresAccommodation;
            },
            set(val) {
                return this.$store.commit('modifyRequiresAccommodation', val);
            }
        },
        roomRequirements: {
            get() {
                return this.$store.getters.roomRequirements;
            },
            set(val) {
                return this.$store.commit('modifyRoomRequirements', val);
            }
        },
        dietaryRequirements: {
            get() {
                return this.$store.getters.dietaryRequirements;
            },
            set(val) {
                return this.$store.commit('modifyDietaryRequirements', val);
            }
        },
        ...mapState({
            event: state => state.event,
            user: state => state.auth.user,
            impersonating: state => state.auth.impersonating,
        }),
        ...mapGetters([
            'can',
        ])
    },
    mounted() {
        console.log("Event user: ", user);
    },
    methods: {
        incrementAdults(e) {
            console.log("incrementing");
            this.adultGuests++;
        },
        decrementAdults(e) {
            this.adultGuests--;
        },
        incrementChildren(e) {
            console.log("incrementing");
            this.childGuests++;
        },
        decrementChildren(e) {
            this.childGuests--;
        },
        validateBeforeSubmit() {
            if(!this.adultGuests) {
                this.adultGuests = 0;
            }

            if(!this.childGuests) {
                this.childGuests = 0;
            }

            this.$validator.validateAll().then(result => {
                if(!result || this.$wait.is('submit')) {
                    return;
                }

                this.submit()
                this.$wait.start('submit');

                console.log("Errors are present: ", result);
            })
        },
        submit() {
            this.registration.eventId = this.event.id;

            if(window.gtag) {
                window.gtag("event", "form_submission", {
                    "event_category": "create",
                    "event_label": "conference_registration"
                });
            }

            API.addRegistration(this.registration).then(response => {
                console.log("Response: " + response);
                this.$store.commit('modifyRegistrationComplete', true);
                this.$router.push('Thanks')
            })
            .catch(e => {
                console.log(e);
            })
            .finally(() => {
                this.$wait.end('submit');
            })
        }
    }
}
</script>

<style scoped>

</style>
